<template>
  <HrPageLayout
    title="Shift Management"
    subtitle="Create and assign employee shifts"
    icon="⏰"
    :breadcrumb-items="[
      { label: 'Leave &amp; Attendance' },
      { label: 'Shift Management' }
    ]"
  >
    <template #header-actions>
      <button class="hr-btn hr-btn-success" @click="openAddModal">
        <i class="bx bx-plus-circle"></i> Add Shift Type
      </button>
    </template>

    <div class="shift-management-content">

      <!-- Alert messages -->
      <div v-if="successMessage" class="hr-alert hr-alert-success hr-mb-3">
        <i class="bx bx-check-circle hr-me-2"></i>{{ successMessage }}
      </div>
      <div v-if="errorMessage" class="hr-alert hr-alert-danger hr-mb-3">
        <i class="bx bx-error-circle hr-me-2"></i>{{ errorMessage }}
      </div>

      <!-- Tabs -->
      <div class="hr-chart-card hr-mb-4">
        <div class="tab-nav">
          <button
            class="tab-btn"
            :class="{ active: activeTab === 'shift-types' }"
            @click="activeTab = 'shift-types'"
          >
            <i class="bx bx-time-five hr-me-2"></i>Shift Types
          </button>
          <button
            class="tab-btn"
            :class="{ active: activeTab === 'assign-shifts' }"
            @click="activeTab = 'assign-shifts'"
          >
            <i class="bx bx-user-plus hr-me-2"></i>Assign Shifts
          </button>
          <button
            class="tab-btn"
            :class="{ active: activeTab === 'current-assignments' }"
            @click="activeTab = 'current-assignments'"
          >
            <i class="bx bx-list-check hr-me-2"></i>Current Assignments
          </button>
        </div>
      </div>

      <!-- TAB 1: Shift Types -->
      <div v-if="activeTab === 'shift-types'">
        <!-- Metrics -->
        <div class="hr-metrics-grid hr-mb-4">
          <div class="hr-metric-card gradient-1">
            <div class="hr-metric-icon"><i class="bx bx-time"></i></div>
            <p class="hr-metric-label">Total Shifts</p>
            <h2 class="hr-metric-value">{{ metrics.total || 0 }}</h2>
          </div>
          <div class="hr-metric-card gradient-2">
            <div class="hr-metric-icon"><i class="bx bx-check-circle"></i></div>
            <p class="hr-metric-label">Active</p>
            <h2 class="hr-metric-value">{{ metrics.active || 0 }}</h2>
          </div>
          <div class="hr-metric-card gradient-3">
            <div class="hr-metric-icon"><i class="bx bx-moon"></i></div>
            <p class="hr-metric-label">Overnight</p>
            <h2 class="hr-metric-value">{{ metrics.overnight || 0 }}</h2>
          </div>
          <div class="hr-metric-card gradient-4">
            <div class="hr-metric-icon"><i class="bx bx-user-check"></i></div>
            <p class="hr-metric-label">Assigned</p>
            <h2 class="hr-metric-value">{{ metrics.assigned || 0 }}</h2>
          </div>
        </div>

        <!-- Shift Types Table -->
        <div class="hr-chart-card">
          <div class="hr-chart-header">
            <h5 class="hr-chart-title"><i class="bx bx-time-five"></i> Shift Types</h5>
          </div>
          <div class="hr-chart-body">
            <div v-if="loading" class="hr-loading-state">
              <div class="hr-spinner"></div>
              <p>Loading shift types...</p>
            </div>
            <div v-else-if="shiftTypes.length === 0" class="hr-empty-state">
              <i class="bx bx-time"></i>
              <h5>No shift types found</h5>
              <p>Add your first shift type to get started</p>
              <button class="hr-btn hr-btn-primary" @click="openAddModal">
                <i class="bx bx-plus hr-me-2"></i> Add Shift Type
              </button>
            </div>
            <div v-else class="hr-table-wrapper">
              <table class="hr-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th>Start Time</th>
                    <th>End Time</th>
                    <th>Working Days</th>
                    <th style="text-align:center;">Break (min)</th>
                    <th style="text-align:center;">Grace In</th>
                    <th style="text-align:center;">Grace Out</th>
                    <th style="text-align:center;">Overnight</th>
                    <th style="text-align:center;">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(shift, index) in shiftTypes" :key="shift.id">
                    <td>{{ index + 1 }}</td>
                    <td><strong>{{ shift.shift_name }}</strong></td>
                    <td>{{ shift.start_time }}</td>
                    <td>{{ shift.end_time }}</td>
                    <td>{{ shift.working_days }}</td>
                    <td style="text-align:center;">{{ shift.break_duration }}</td>
                    <td style="text-align:center;">{{ shift.grace_period_in }} min</td>
                    <td style="text-align:center;">{{ shift.grace_period_out }} min</td>
                    <td style="text-align:center;">
                      <span class="hr-badge" :class="shift.is_overnight == 1 ? 'hr-badge-warning' : 'hr-badge-muted'">
                        {{ shift.is_overnight == 1 ? 'Yes' : 'No' }}
                      </span>
                    </td>
                    <td style="text-align:center;">
                      <div class="hr-flex hr-flex-center hr-gap-2" style="justify-content:center;">
                        <button class="hr-btn hr-btn-info hr-btn-sm hr-btn-icon" @click="openEditModal(shift)" title="Edit">
                          <i class="bx bx-edit"></i>
                        </button>
                        <button class="hr-btn hr-btn-danger hr-btn-sm hr-btn-icon" @click="confirmDelete(shift)" title="Delete">
                          <i class="bx bx-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <!-- TAB 2: Assign Shifts -->
      <div v-if="activeTab === 'assign-shifts'">
        <div class="hr-chart-card">
          <div class="hr-chart-header">
            <h5 class="hr-chart-title"><i class="bx bx-user-plus"></i> Assign Shifts</h5>
          </div>
          <div class="hr-chart-body">

            <!-- Employee Search Bar -->
            <div class="employee-search-section hr-mb-4">
              <div class="hr-flex hr-flex-center hr-gap-3 hr-mb-3">
                <div class="hr-search-wrap" style="flex: 1; max-width: 400px;">
                  <input
                    v-model="employeeSearchQuery"
                    @input="searchEmployees"
                    type="text"
                    class="hr-input"
                    placeholder="Search employees by name, ID, department..."
                  >
                  <i class="bx bx-search-alt search-icon"></i>
                </div>
                <select class="hr-select" style="width: 180px;" v-model="employeeSearchFilters.department" @change="searchEmployees">
                  <option value="">All Departments</option>
                  <option v-for="dept in availableFilters.departments" :key="dept" :value="dept">{{ dept }}</option>
                </select>
                <select class="hr-select" style="width: 180px;" v-model="employeeSearchFilters.userProfile" @change="searchEmployees">
                  <option value="">All User Profiles</option>
                  <option v-for="profile in availableFilters.userProfiles" :key="profile.id" :value="profile.id">{{ profile.profile_name }}</option>
                </select>
                <button class="hr-btn hr-btn-secondary hr-btn-sm" @click="clearEmployeeSearch">
                  <i class="bx bx-x hr-me-1"></i> Clear
                </button>
              </div>
              
              <!-- Search Results -->
              <div v-if="employeeSearchResults.length > 0" class="search-results-card">
                <div class="search-results-header">
                  <h6><i class="bx bx-search hr-me-2"></i>Search Results ({{ employeeSearchPagination.total }})</h6>
                  <div class="pagination-info">
                    Page {{ employeeSearchPagination.page }} of {{ employeeSearchPagination.totalPages }}
                  </div>
                </div>
                <div class="search-results-table">
                  <table class="hr-table hr-table-sm">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Emp No</th>
                        <th>Department</th>
                        <th>User Profile</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="emp in employeeSearchResults" :key="emp.id">
                        <td>{{ emp.name }}</td>
                        <td>{{ emp.empNo }}</td>
                        <td>{{ emp.department }}</td>
                        <td>{{ emp.user_profile || '-' }}</td>
                        <td>
                          <button 
                            class="hr-btn hr-btn-primary hr-btn-xs" 
                            @click="quickAssignEmployee(emp)"
                            title="Quick assign shift"
                          >
                            <i class="bx bx-plus"></i> Assign
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div class="search-pagination" v-if="employeeSearchPagination.totalPages > 1">
                  <button 
                    class="hr-btn hr-btn-sm hr-btn-secondary" 
                    :disabled="employeeSearchPagination.page <= 1"
                    @click="changeSearchPage(employeeSearchPagination.page - 1)"
                  >
                    <i class="bx bx-chevron-left"></i>
                  </button>
                  <span class="pagination-text">
                    {{ employeeSearchPagination.page }} / {{ employeeSearchPagination.totalPages }}
                  </span>
                  <button 
                    class="hr-btn hr-btn-sm hr-btn-secondary" 
                    :disabled="employeeSearchPagination.page >= employeeSearchPagination.totalPages"
                    @click="changeSearchPage(employeeSearchPagination.page + 1)"
                  >
                    <i class="bx bx-chevron-right"></i>
                  </button>
                </div>
              </div>
            </div>

            <!-- Assignment Mode Selector -->
            <div class="hr-mb-4">
              <label class="hr-form-label">Assignment Mode</label>
              <div class="assign-mode-btns">
                <button
                  class="hr-btn hr-btn-sm"
                  :class="assignMode === 'individual' ? 'hr-btn-primary' : 'hr-btn-secondary'"
                  @click="assignMode = 'individual'"
                >
                  <i class="bx bx-user hr-me-1"></i> Individual Employee
                </button>
                <button
                  class="hr-btn hr-btn-sm"
                  :class="assignMode === 'user-profile' ? 'hr-btn-primary' : 'hr-btn-secondary'"
                  @click="assignMode = 'user-profile'"
                >
                  <i class="bx bx-user-circle hr-me-1"></i> By User Profile
                </button>
                <button
                  class="hr-btn hr-btn-sm"
                  :class="assignMode === 'department' ? 'hr-btn-primary' : 'hr-btn-secondary'"
                  @click="assignMode = 'department'"
                >
                  <i class="bx bx-building hr-me-1"></i> By Department
                </button>
                <button
                  class="hr-btn hr-btn-sm"
                  :class="assignMode === 'multi' ? 'hr-btn-primary' : 'hr-btn-secondary'"
                  @click="assignMode = 'multi'"
                >
                  <i class="bx bx-select-multiple hr-me-1"></i> Multi-Select
                </button>
              </div>
            </div>

            <!-- Individual Mode -->
            <div v-if="assignMode === 'individual'" class="assign-form-grid">
              <div>
                <label class="hr-form-label">Employee <span class="required">*</span></label>
                <select class="hr-select" v-model="assignForm.employeeId">
                  <option value="">-- Select Employee --</option>
                  <option v-for="emp in employees" :key="emp.nationalId" :value="emp.nationalId">
                    {{ emp.fullName }} ({{ emp.empNo }})
                  </option>
                </select>
              </div>
              <div>
                <label class="hr-form-label">Shift Type <span class="required">*</span></label>
                <select class="hr-select" v-model="assignForm.shiftTypeId">
                  <option value="">-- Select Shift --</option>
                  <option v-for="st in shiftTypes" :key="st.id" :value="st.id">{{ st.shift_name }}</option>
                </select>
              </div>
            </div>

            <!-- By User Profile Mode -->
            <div v-if="assignMode === 'user-profile'" class="assign-form-grid">
              <div>
                <label class="hr-form-label">User Profile <span class="required">*</span></label>
                <select class="hr-select" v-model="assignForm.userProfileId">
                  <option value="">-- Select User Profile --</option>
                  <option v-for="profile in userProfiles" :key="profile.id" :value="profile.id">
                    {{ profile.profile_name }} <span v-if="profile.description">({{ profile.description }})</span>
                  </option>
                </select>
              </div>
              <div>
                <label class="hr-form-label">Shift Type <span class="required">*</span></label>
                <select class="hr-select" v-model="assignForm.shiftTypeId">
                  <option value="">-- Select Shift --</option>
                  <option v-for="st in shiftTypes" :key="st.id" :value="st.id">{{ st.shift_name }}</option>
                </select>
              </div>
            </div>

            <!-- By Department Mode -->
            <div v-if="assignMode === 'department'" class="assign-form-grid">
              <div>
                <label class="hr-form-label">Department <span class="required">*</span></label>
                <select class="hr-select" v-model="assignForm.departmentId">
                  <option value="">-- Select Department --</option>
                  <option v-for="dept in departments" :key="dept.id" :value="dept.id">{{ dept.departmentName }}</option>
                </select>
              </div>
              <div>
                <label class="hr-form-label">Shift Type <span class="required">*</span></label>
                <select class="hr-select" v-model="assignForm.shiftTypeId">
                  <option value="">-- Select Shift --</option>
                  <option v-for="st in shiftTypes" :key="st.id" :value="st.id">{{ st.shift_name }}</option>
                </select>
              </div>
            </div>

            <!-- Multi-Select Mode -->
            <div v-if="assignMode === 'multi'">
              <div class="hr-mb-3">
                <label class="hr-form-label">Search Employees</label>
                <input
                  type="text"
                  class="hr-input"
                  v-model="employeeSearch"
                  placeholder="Search by name or emp no..."
                >
              </div>
              <div class="employee-checklist hr-mb-3">
                <label
                  v-for="emp in filteredEmployeeList"
                  :key="emp.nationalId"
                  class="employee-check-item"
                >
                  <input
                    type="checkbox"
                    :value="emp.nationalId"
                    v-model="assignForm.selectedEmployeeIds"
                  >
                  <span>{{ emp.fullName }} <small class="hr-text-muted">({{ emp.empNo }})</small></span>
                </label>
                <div v-if="filteredEmployeeList.length === 0" class="hr-text-muted" style="padding:12px;">
                  No employees found
                </div>
              </div>
              <div>
                <label class="hr-form-label">Shift Type <span class="required">*</span></label>
                <select class="hr-select" v-model="assignForm.shiftTypeId">
                  <option value="">-- Select Shift --</option>
                  <option v-for="st in shiftTypes" :key="st.id" :value="st.id">{{ st.shift_name }}</option>
                </select>
              </div>
              <p class="hr-text-muted hr-mt-2">
                <i class="bx bx-info-circle hr-me-1"></i>
                {{ assignForm.selectedEmployeeIds.length }} employee(s) selected
              </p>
            </div>

            <!-- Remote Shift Details -->
            <div class="assign-form-grid hr-mt-4" style="background:#f8fafc; padding:15px; border-radius:8px; border:1px solid #e2e8f0;">
              <div>
                <label class="hr-form-label">
                  <input type="checkbox" v-model="assignForm.isRemote" class="hr-checkbox">
                  Is Remote Shift?
                </label>
                <p class="hr-text-muted hr-mt-1" style="font-size: 12px;">Enables automatic attendance generation for remote employees.</p>
              </div>
              <div v-if="assignForm.isRemote">
                <label class="hr-form-label">Remote Location <span class="required">*</span></label>
                <select class="hr-select" v-model="assignForm.remoteLocationId">
                  <option value="">-- Select Remote Location --</option>
                  <option v-for="loc in remoteLocations" :key="loc.id" :value="loc.id">{{ loc.location_name }}</option>
                </select>
              </div>
            </div>

            <div class="hr-mt-4">
              <button class="hr-btn hr-btn-success" @click="submitAssignment" :disabled="assigning">
                <span v-if="assigning" class="hr-spinner-inline"></span>
                <i v-else class="bx bx-check hr-me-2"></i>
                Assign Shift
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- TAB 3: Current Assignments -->
      <div v-if="activeTab === 'current-assignments'">
        <div class="hr-chart-card">
          <div class="hr-chart-header">
            <h5 class="hr-chart-title"><i class="bx bx-list-check"></i> Current Assignments</h5>
            <div class="hr-flex hr-flex-center hr-gap-2">
              <div class="hr-search-wrap" style="min-width:220px;">
                <input
                  v-model="assignmentSearch"
                  type="text"
                  class="hr-input"
                  placeholder="Search employee..."
                >
                <i class="bx bx-search-alt search-icon"></i>
              </div>
              <select class="hr-select" style="width:180px;" v-model="assignmentUserProfileFilter">
                <option value="">All User Profiles</option>
                <option v-for="profile in userProfiles" :key="profile.id" :value="profile.id">
                  {{ profile.profile_name }}
                </option>
              </select>
            </div>
          </div>
          <div class="hr-chart-body">
            <div v-if="loading" class="hr-loading-state">
              <div class="hr-spinner"></div>
              <p>Loading assignments...</p>
            </div>
            <div v-else-if="filteredAssignments.length === 0" class="hr-empty-state">
              <i class="bx bx-user-x"></i>
              <h5>No assignments found</h5>
              <p>Assign shifts to employees in the Assign Shifts tab</p>
            </div>
            <div v-else class="hr-table-wrapper">
              <table class="hr-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Employee Name</th>
                    <th>Emp No</th>
                    <th>Department</th>
                    <th>User Profile</th>
                    <th>Shift Name</th>
                    <th>Start Time</th>
                    <th>End Time</th>
                    <th style="text-align:center;">Overnight</th>
                    <th style="text-align:center;">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(asgn, index) in filteredAssignments" :key="asgn.employee_id">
                    <td>{{ ((assignmentPagination.page - 1) * assignmentPagination.pageSize) + index + 1 }}</td>
                    <td>{{ asgn.employee_name }}</td>
                    <td>{{ asgn.empNo }}</td>
                    <td>{{ asgn.departmentName }}</td>
                    <td>{{ asgn.user_profile || '-' }}</td>
                    <td><strong>{{ asgn.shift_name }}</strong></td>
                    <td>{{ asgn.start_time }}</td>
                    <td>{{ asgn.end_time }}</td>
                    <td style="text-align:center;">
                      <span class="hr-badge" :class="asgn.is_overnight == 1 ? 'hr-badge-warning' : 'hr-badge-muted'">
                        {{ asgn.is_overnight == 1 ? 'Yes' : 'No' }}
                      </span>
                    </td>
                    <td style="text-align:center;">
                      <button class="hr-btn hr-btn-danger hr-btn-sm hr-btn-icon" @click="confirmRemove(asgn)" title="Remove">
                        <i class="bx bx-trash"></i>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
              
              <!-- Pagination -->
              <div v-if="assignmentPagination.totalPages > 1" class="hr-pagination">
                <button 
                  class="hr-btn hr-btn-sm hr-btn-secondary" 
                  :disabled="assignmentPagination.page <= 1"
                  @click="changeAssignmentPage(assignmentPagination.page - 1)"
                >
                  <i class="bx bx-chevron-left"></i> Previous
                </button>
                <span class="pagination-info">
                  Page {{ assignmentPagination.page }} of {{ assignmentPagination.totalPages }} 
                  ({{ assignmentPagination.total }} total)
                </span>
                <button 
                  class="hr-btn hr-btn-sm hr-btn-secondary" 
                  :disabled="assignmentPagination.page >= assignmentPagination.totalPages"
                  @click="changeAssignmentPage(assignmentPagination.page + 1)"
                >
                  Next <i class="bx bx-chevron-right"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div><!-- end shift-management-content -->

    <!-- ============================================================ -->
    <!-- MODALS (all teleported to body)                              -->
    <!-- ============================================================ -->
    <Teleport to="body">

      <!-- 1. Add/Edit Shift Type Modal -->
      <div class="modal fade" id="shift-type-modal" tabindex="-1" data-bs-backdrop="static">
        <div class="modal-dialog modal-lg">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">{{ isEditing ? 'Edit Shift Type' : 'Add Shift Type' }}</h5>
              <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
            </div>
            <div class="modal-body">
              <div class="row g-3">
                <div class="col-md-12">
                  <label class="hr-form-label">Shift Name <span class="required">*</span></label>
                  <input type="text" class="hr-input" v-model="shiftTypeForm.shiftName" placeholder="e.g., Morning Shift">
                </div>
                <div class="col-md-6">
                  <label class="hr-form-label">Start Time <span class="required">*</span></label>
                  <input type="time" class="hr-input" v-model="shiftTypeForm.startTime">
                </div>
                <div class="col-md-6">
                  <label class="hr-form-label">End Time <span class="required">*</span></label>
                  <input type="time" class="hr-input" v-model="shiftTypeForm.endTime">
                </div>
                <div class="col-md-12">
                  <label class="hr-form-label">Working Days</label>
                  <div class="working-days-grid">
                    <label v-for="day in weekDays" :key="day.value" class="day-check-item">
                      <input
                        type="checkbox"
                        :value="day.value"
                        :checked="selectedDays.includes(day.value)"
                        @change="toggleDay(day.value)"
                      >
                      <span>{{ day.label }}</span>
                    </label>
                  </div>
                </div>
                <div class="col-md-4">
                  <label class="hr-form-label">Break Duration (min)</label>
                  <input type="number" class="hr-input" v-model="shiftTypeForm.breakDuration" min="0">
                </div>
                <div class="col-md-4">
                  <label class="hr-form-label">Grace Period In (min)</label>
                  <input type="number" class="hr-input" v-model="shiftTypeForm.gracePeriodIn" min="0">
                </div>
                <div class="col-md-4">
                  <label class="hr-form-label">Grace Period Out (min)</label>
                  <input type="number" class="hr-input" v-model="shiftTypeForm.gracePeriodOut" min="0">
                </div>
                <div class="col-md-12">
                  <label class="hr-checkbox-wrap">
                    <input type="checkbox" v-model="shiftTypeForm.isOvernight">
                    <span>Is Overnight Shift</span>
                  </label>
                  <small class="hr-form-hint">Check if the shift spans midnight</small>
                </div>
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="hr-btn hr-btn-secondary" data-bs-dismiss="modal">Cancel</button>
              <button type="button" class="hr-btn" :class="isEditing ? 'hr-btn-info' : 'hr-btn-success'" :disabled="saving" @click="saveShiftType">
                <span v-if="saving" class="hr-spinner-inline"></span>
                <i v-else class="bx bx-check hr-me-2"></i>
                {{ isEditing ? 'Update' : 'Save' }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- 2. Delete Shift Type Modal -->
      <div class="modal fade" id="delete-shift-type-modal" tabindex="-1">
        <div class="modal-dialog modal-sm">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">Confirm Delete</h5>
              <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
            </div>
            <div class="modal-body text-center">
              <i class="bx bx-error-circle modal-danger-icon"></i>
              <h5>Are you sure?</h5>
              <p class="hr-text-muted">
                You are about to delete <strong>{{ deleteTarget?.shift_name }}</strong>. This cannot be undone.
              </p>
            </div>
            <div class="modal-footer">
              <button type="button" class="hr-btn hr-btn-secondary" data-bs-dismiss="modal">Cancel</button>
              <button type="button" class="hr-btn hr-btn-danger" :disabled="deleting" @click="deleteShiftType">
                <span v-if="deleting" class="hr-spinner-inline"></span>
                <i v-else class="bx bx-trash hr-me-2"></i>
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- 3. Assign Confirm Modal -->
      <div class="modal fade" id="assign-confirm-modal" tabindex="-1">
        <div class="modal-dialog modal-sm">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">Confirm Assignment</h5>
              <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
            </div>
            <div class="modal-body text-center">
              <i class="bx bx-user-check modal-success-icon"></i>
              <h5>Assign Shift?</h5>
              <p class="hr-text-muted" v-if="assignMode === 'individual'">
                Assign <strong>{{ selectedShiftName }}</strong> to the selected employee?
              </p>
              <p class="hr-text-muted" v-else-if="assignMode === 'multi'">
                Assign <strong>{{ selectedShiftName }}</strong> to
                <strong>{{ assignForm.selectedEmployeeIds.length }}</strong> selected employee(s)?
              </p>
              <p class="hr-text-muted" v-else-if="assignMode === 'user-profile'">
                Assign <strong>{{ selectedShiftName }}</strong> to all employees with the selected user profile?
              </p>
              <p class="hr-text-muted" v-else>
                Assign <strong>{{ selectedShiftName }}</strong> to all employees in the selected department?
              </p>
            </div>
            <div class="modal-footer">
              <button type="button" class="hr-btn hr-btn-secondary" data-bs-dismiss="modal">Cancel</button>
              <button type="button" class="hr-btn hr-btn-success" :disabled="assigning" @click="executeAssignment">
                <span v-if="assigning" class="hr-spinner-inline"></span>
                <i v-else class="bx bx-check hr-me-2"></i>
                Confirm
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- 4. Assign Result Modal -->
      <div class="modal fade" id="assign-result-modal" tabindex="-1">
        <div class="modal-dialog modal-sm">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">Assignment Result</h5>
              <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
            </div>
            <div class="modal-body text-center">
              <i class="bx bx-check-circle modal-success-icon"></i>
              <h5>Done!</h5>
              <p>
                <span class="hr-badge hr-badge-success">{{ bulkResult.successCount }} succeeded</span>
                <span v-if="bulkResult.failureCount > 0" class="hr-badge hr-badge-danger hr-ms-2">
                  {{ bulkResult.failureCount }} failed
                </span>
              </p>
              <div v-if="bulkResult.failures && bulkResult.failures.length > 0" class="hr-mt-2">
                <small class="hr-text-muted">Failed employees:</small>
                <ul class="text-start" style="font-size:12px;">
                  <li v-for="f in bulkResult.failures" :key="f.employee_id">
                    {{ f.employee_id }}: {{ f.reason }}
                  </li>
                </ul>
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="hr-btn hr-btn-success" data-bs-dismiss="modal" @click="fetchAssignments">OK</button>
            </div>
          </div>
        </div>
      </div>

      <!-- 5. Remove Assignment Modal -->
      <div class="modal fade" id="remove-assignment-modal" tabindex="-1">
        <div class="modal-dialog modal-sm">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">Remove Assignment</h5>
              <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
            </div>
            <div class="modal-body text-center">
              <i class="bx bx-error-circle modal-danger-icon"></i>
              <h5>Remove Shift?</h5>
              <p class="hr-text-muted">
                Remove shift assignment for <strong>{{ removeTarget?.employee_name }}</strong>?
              </p>
            </div>
            <div class="modal-footer">
              <button type="button" class="hr-btn hr-btn-secondary" data-bs-dismiss="modal">Cancel</button>
              <button type="button" class="hr-btn hr-btn-danger" :disabled="deleting" @click="removeAssignment">
                <span v-if="deleting" class="hr-spinner-inline"></span>
                <i v-else class="bx bx-trash hr-me-2"></i>
                Remove
              </button>
            </div>
          </div>
        </div>
      </div>

    </Teleport>

  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { authUrl, execute } from "@/api";
import "@/assets/css/hr-page-shared.css";

export default {
  name: "ShiftManagement",
  components: { HrPageLayout },
  data() {
    return {
      activeTab: 'shift-types',
      loading: false,
      saving: false,
      deleting: false,
      assigning: false,

      // Shift Types
      shiftTypes: [],
      remoteLocations: [],
      metrics: { total: 0, active: 0, overnight: 0, assigned: 0 },

      // Assign tab
      assignMode: 'individual',
      userProfiles: [],
      departments: [],
      employees: [],
      assignForm: {
        shiftTypeId: '',
        employeeId: '',
        userProfileId: '',
        departmentId: '',
        selectedEmployeeIds: [],
        isRemote: false,
        remoteLocationId: ''
      },
      bulkResult: { successCount: 0, failureCount: 0, failures: [] },

      // Employee Search
      employeeSearchQuery: '',
      employeeSearchFilters: {
        department: '',
        userProfile: '',
        employmentStatus: '',
        position: ''
      },
      employeeSearchResults: [],
      employeeSearchPagination: { page: 1, pageSize: 50, total: 0, totalPages: 0 },
      availableFilters: {
        departments: [],
        userProfiles: [],
        positions: [],
        employmentStatuses: []
      },
      searchLoading: false,

      // Assignments tab
      assignments: [],
      assignmentSearch: '',
      assignmentDeptFilter: '',
      assignmentUserProfileFilter: '',
      assignmentPagination: { page: 1, pageSize: 50, total: 0, totalPages: 0 },

      // Form state
      isEditing: false,
      editingId: null,
      shiftTypeForm: {
        shiftName: '',
        startTime: '',
        endTime: '',
        isOvernight: false,
        workingDays: 'Mon-Fri',
        breakDuration: 60,
        gracePeriodIn: 15,
        gracePeriodOut: 15,
      },
      selectedDays: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
      deleteTarget: null,
      removeTarget: null,
      successMessage: '',
      errorMessage: '',
      employeeSearch: '',

      weekDays: [
        { value: 'Mon', label: 'Mon' },
        { value: 'Tue', label: 'Tue' },
        { value: 'Wed', label: 'Wed' },
        { value: 'Thu', label: 'Thu' },
        { value: 'Fri', label: 'Fri' },
        { value: 'Sat', label: 'Sat' },
        { value: 'Sun', label: 'Sun' },
      ],
    };
  },
  computed: {
    filteredAssignments() {
      // Server-side filtering is now handled in fetchAssignments
      return this.assignments;
    },
    filteredEmployeeList() {
      if (!this.employeeSearch) return this.employees;
      const q = this.employeeSearch.toLowerCase();
      return this.employees.filter(e =>
        (e.fullName || '').toLowerCase().includes(q) ||
        (e.empNo || '').toLowerCase().includes(q)
      );
    },
    selectedShiftName() {
      const st = this.shiftTypes.find(s => s.id == this.assignForm.shiftTypeId);
      return st ? st.shift_name : '';
    },
  },
  mounted() {
    this.loadInitialData();
  },
  watch: {
    // Reload dropdowns when switching to assign-shifts tab
    activeTab(newTab) {
      if (newTab === 'assign-shifts' && this.departments.length === 0) {
        this.fetchDropdowns();
      }
    }
  },
  methods: {
    // ── Data Loading ──────────────────────────────────────────────
    async loadInitialData() {
      await Promise.all([
        this.fetchShiftTypes(),
        this.fetchAssignments(),
        this.fetchDropdowns(),
        this.fetchRemoteLocations(),
      ]);
    },

    async fetchRemoteLocations() {
      const data = new FormData();
      data.append('function', 'getRemoteLocations');
      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          this.remoteLocations = res.data.data.locations || [];
        }
      } catch (e) {
        // ignore
      }
    },

    async fetchShiftTypes() {
      this.loading = true;
      const data = new FormData();
      data.append('function', 'getShiftTypes');
      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          this.shiftTypes = res.data.data.shiftTypes || [];
          this.metrics = res.data.data.metrics || { total: 0, active: 0, overnight: 0, assigned: 0 };
        } else {
          this.showError(res.data.message || 'Failed to load shift types');
        }
      } catch (e) {
        this.showError('Failed to load shift types');
      } finally {
        this.loading = false;
      }
    },

    async fetchAssignments() {
      const data = new FormData();
      data.append('function', 'getShiftAssignments');
      data.append('page', this.assignmentPagination.page);
      data.append('pageSize', this.assignmentPagination.pageSize);
      data.append('searchQuery', this.assignmentSearch);
      data.append('departmentFilter', this.assignmentDeptFilter);
      data.append('userProfileFilter', this.assignmentUserProfileFilter);
      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          this.assignments = res.data.data.assignments || [];
          this.assignmentPagination = res.data.data.pagination || { page: 1, pageSize: 50, total: 0, totalPages: 0 };
        }
      } catch (e) {
        // silent
      }
    },

    async fetchDropdowns() {
      const data = new FormData();
      data.append('function', 'getShiftManagementDropdowns');
      try {
        const res = await execute(data, authUrl);
        console.log('Dropdowns response:', res.data); // Debug log
        if (res.data.success) {
          this.userProfiles = res.data.data.userProfiles || [];
          this.departments = res.data.data.departments || [];
          this.employees = res.data.data.employees || [];
          console.log('Loaded:', {
            userProfiles: this.userProfiles.length,
            departments: this.departments.length,
            employees: this.employees.length
          }); // Debug log
        } else {
          console.error('Failed to load dropdowns:', res.data.message);
          this.showError(res.data.message || 'Failed to load dropdowns');
        }
      } catch (e) {
        console.error('Error fetching dropdowns:', e);
        this.showError('Failed to load dropdowns');
      }
    },

    // ── Shift Type CRUD ───────────────────────────────────────────
    openAddModal() {
      this.isEditing = false;
      this.editingId = null;
      this.shiftTypeForm = {
        shiftName: '',
        startTime: '',
        endTime: '',
        isOvernight: false,
        workingDays: 'Mon-Fri',
        breakDuration: 60,
        gracePeriodIn: 15,
        gracePeriodOut: 15,
      };
      this.selectedDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
      this.$nextTick(() => {
        $('#shift-type-modal').modal('show');
      });
    },

    openEditModal(shift) {
      this.isEditing = true;
      this.editingId = shift.id;
      this.shiftTypeForm = {
        shiftName: shift.shift_name,
        startTime: shift.start_time,
        endTime: shift.end_time,
        isOvernight: shift.is_overnight == 1,
        workingDays: shift.working_days || 'Mon-Fri',
        breakDuration: shift.break_duration || 60,
        gracePeriodIn: shift.grace_period_in || 15,
        gracePeriodOut: shift.grace_period_out || 15,
      };
      // Parse working days string into array
      this.selectedDays = this.parseDaysString(shift.working_days || 'Mon-Fri');
      this.$nextTick(() => {
        $('#shift-type-modal').modal('show');
      });
    },

    parseDaysString(str) {
      const allDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
      if (!str) return ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
      // If it's a range like "Mon-Fri"
      if (str.includes('-') && !str.includes(',')) {
        const parts = str.split('-');
        if (parts.length === 2) {
          const start = allDays.indexOf(parts[0]);
          const end = allDays.indexOf(parts[1]);
          if (start !== -1 && end !== -1) {
            return allDays.slice(start, end + 1);
          }
        }
      }
      // Comma-separated
      return str.split(',').map(d => d.trim()).filter(d => allDays.includes(d));
    },

    buildDaysString() {
      const allDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
      const sorted = allDays.filter(d => this.selectedDays.includes(d));
      if (sorted.length === 0) return '';
      // Try to express as range
      if (sorted.length === 5 && sorted.join(',') === 'Mon,Tue,Wed,Thu,Fri') return 'Mon-Fri';
      if (sorted.length === 6 && sorted.join(',') === 'Mon,Tue,Wed,Thu,Fri,Sat') return 'Mon-Sat';
      if (sorted.length === 7) return 'Mon-Sun';
      return sorted.join(',');
    },

    toggleDay(day) {
      const idx = this.selectedDays.indexOf(day);
      if (idx === -1) {
        this.selectedDays.push(day);
      } else {
        this.selectedDays.splice(idx, 1);
      }
    },

    validateShiftTypeForm() {
      if (!this.shiftTypeForm.shiftName.trim()) return 'Shift name is required';
      if (!this.shiftTypeForm.startTime) return 'Start time is required';
      if (!this.shiftTypeForm.endTime) return 'End time is required';
      if (this.shiftTypeForm.breakDuration < 0) return 'Break duration must be non-negative';
      if (this.shiftTypeForm.gracePeriodIn < 0) return 'Grace period in must be non-negative';
      if (this.shiftTypeForm.gracePeriodOut < 0) return 'Grace period out must be non-negative';
      return null;
    },

    async saveShiftType() {
      const err = this.validateShiftTypeForm();
      if (err) { this.showError(err); return; }

      this.saving = true;
      const data = new FormData();
      data.append('function', this.isEditing ? 'updateShiftType' : 'addShiftType');
      if (this.isEditing) data.append('id', this.editingId);
      data.append('shiftName', this.shiftTypeForm.shiftName.trim());
      data.append('startTime', this.shiftTypeForm.startTime);
      data.append('endTime', this.shiftTypeForm.endTime);
      data.append('isOvernight', this.shiftTypeForm.isOvernight ? '1' : '0');
      data.append('workingDays', this.buildDaysString());
      data.append('breakDuration', this.shiftTypeForm.breakDuration);
      data.append('gracePeriodIn', this.shiftTypeForm.gracePeriodIn);
      data.append('gracePeriodOut', this.shiftTypeForm.gracePeriodOut);

      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          $('#shift-type-modal').modal('hide');
          this.showSuccess(res.data.message || 'Shift type saved successfully');
          await this.fetchShiftTypes();
        } else {
          this.showError(res.data.message || 'Failed to save shift type');
        }
      } catch (e) {
        this.showError('Failed to save shift type');
      } finally {
        this.saving = false;
      }
    },

    confirmDelete(shift) {
      this.deleteTarget = shift;
      this.$nextTick(() => {
        $('#delete-shift-type-modal').modal('show');
      });
    },

    async deleteShiftType() {
      if (!this.deleteTarget) return;
      this.deleting = true;
      const data = new FormData();
      data.append('function', 'deleteShiftType');
      data.append('id', this.deleteTarget.id);
      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          $('#delete-shift-type-modal').modal('hide');
          this.showSuccess(res.data.message || 'Shift type deleted');
          this.deleteTarget = null;
          await this.fetchShiftTypes();
        } else {
          this.showError(res.data.message || 'Failed to delete shift type');
        }
      } catch (e) {
        this.showError('Failed to delete shift type');
      } finally {
        this.deleting = false;
      }
    },

    // ── Assignment ────────────────────────────────────────────────
    submitAssignment() {
      // Validate
      if (!this.assignForm.shiftTypeId) {
        this.showError('Please select a shift type');
        return;
      }
      if (this.assignMode === 'individual' && !this.assignForm.employeeId) {
        this.showError('Please select an employee');
        return;
      }
      if (this.assignMode === 'user-profile' && !this.assignForm.userProfileId) {
        this.showError('Please select a user profile');
        return;
      }
      if (this.assignMode === 'department' && !this.assignForm.departmentId) {
        this.showError('Please select a department');
        return;
      }
      if (this.assignMode === 'multi' && this.assignForm.selectedEmployeeIds.length === 0) {
        this.showError('Please select at least one employee');
        return;
      }
      this.$nextTick(() => {
        $('#assign-confirm-modal').modal('show');
      });
    },

    async executeAssignment() {
      this.assigning = true;
      $('#assign-confirm-modal').modal('hide');

      const data = new FormData();
      let fnName = '';

      if (this.assignMode === 'individual') {
        fnName = 'assignShiftToEmployee';
        data.append('employeeId', this.assignForm.employeeId);
        data.append('shiftTypeId', this.assignForm.shiftTypeId);
      } else if (this.assignMode === 'user-profile') {
        fnName = 'bulkAssignShiftByUserProfile';
        data.append('userProfileId', this.assignForm.userProfileId);
        data.append('shiftTypeId', this.assignForm.shiftTypeId);
      } else if (this.assignMode === 'department') {
        fnName = 'bulkAssignShiftByDepartment';
        data.append('departmentId', this.assignForm.departmentId);
        data.append('shiftTypeId', this.assignForm.shiftTypeId);
      } else if (this.assignMode === 'multi') {
        fnName = 'bulkAssignShiftToSelected';
        data.append('nationalIds', JSON.stringify(this.assignForm.selectedEmployeeIds));
        data.append('shiftTypeId', this.assignForm.shiftTypeId);
      }

      data.append('function', fnName);
      data.append('isRemote', this.assignForm.isRemote ? 1 : 0);
      data.append('remoteLocationId', this.assignForm.remoteLocationId || '');

      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          if (this.assignMode === 'individual') {
            this.bulkResult = { successCount: 1, failureCount: 0, failures: [] };
          } else {
            this.bulkResult = {
              successCount: res.data.data?.successCount || 0,
              failureCount: res.data.data?.failureCount || 0,
              failures: res.data.data?.failures || [],
            };
          }
          this.$nextTick(() => {
            $('#assign-result-modal').modal('show');
          });
          await this.fetchAssignments();
          await this.fetchShiftTypes();
        } else {
          this.showError(res.data.message || 'Assignment failed');
        }
      } catch (e) {
        this.showError('Assignment failed');
      } finally {
        this.assigning = false;
      }
    },

    confirmRemove(assignment) {
      this.removeTarget = assignment;
      this.$nextTick(() => {
        $('#remove-assignment-modal').modal('show');
      });
    },

    async removeAssignment() {
      if (!this.removeTarget) return;
      this.deleting = true;
      const data = new FormData();
      data.append('function', 'removeShiftAssignment');
      data.append('id', this.removeTarget.id);
      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          $('#remove-assignment-modal').modal('hide');
          this.showSuccess(res.data.message || 'Assignment removed');
          this.removeTarget = null;
          await this.fetchAssignments();
          await this.fetchShiftTypes();
        } else {
          this.showError(res.data.message || 'Failed to remove assignment');
        }
      } catch (e) {
        this.showError('Failed to remove assignment');
      } finally {
        this.deleting = false;
      }
    },

    // ── Notifications ─────────────────────────────────────────────
    showSuccess(msg) {
      this.successMessage = msg;
      this.errorMessage = '';
      this.showTemporaryMessage(msg, 'success');
    },

    showError(msg) {
      this.errorMessage = msg;
      this.successMessage = '';
      this.showTemporaryMessage(msg, 'danger');
    },

    showTemporaryMessage(message, type = 'info') {
      const alertDiv = document.createElement('div');
      alertDiv.className = `alert alert-${type} alert-dismissible fade show position-fixed`;
      alertDiv.style.cssText = 'top: 20px; right: 20px; z-index: 9999; min-width: 300px;';
      alertDiv.innerHTML = `
        ${message}
        <button type="button" class="btn-close" data-bs-dismiss="alert"></button>
      `;
      document.body.appendChild(alertDiv);
      setTimeout(() => {
        if (alertDiv.parentNode) alertDiv.parentNode.removeChild(alertDiv);
      }, 5000);
    },

    // ── Employee Search ───────────────────────────────────────────
    async searchEmployees() {
      if (this.searchTimeout) clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(async () => {
        await this.performEmployeeSearch();
      }, 300);
    },

    async performEmployeeSearch() {
      this.searchLoading = true;
      const data = new FormData();
      data.append('function', 'searchEmployees');
      data.append('searchQuery', this.employeeSearchQuery);
      data.append('department', this.employeeSearchFilters.department);
      data.append('userProfileId', this.employeeSearchFilters.userProfile);
      data.append('employmentStatus', this.employeeSearchFilters.employmentStatus);
      data.append('position', this.employeeSearchFilters.position);
      data.append('page', this.employeeSearchPagination.page);
      data.append('pageSize', this.employeeSearchPagination.pageSize);

      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          this.employeeSearchResults = res.data.data.employees || [];
          this.employeeSearchPagination = res.data.data.pagination || { page: 1, pageSize: 50, total: 0, totalPages: 0 };
          this.availableFilters = res.data.data.filters || { departments: [], userProfiles: [], positions: [], employmentStatuses: [] };
        }
      } catch (e) {
        // silent
      } finally {
        this.searchLoading = false;
      }
    },

    clearEmployeeSearch() {
      this.employeeSearchQuery = '';
      this.employeeSearchFilters = {
        department: '',
        userProfile: '',
        employmentStatus: '',
        position: ''
      };
      this.employeeSearchResults = [];
      this.employeeSearchPagination = { page: 1, pageSize: 50, total: 0, totalPages: 0 };
    },

    changeSearchPage(page) {
      this.employeeSearchPagination.page = page;
      this.performEmployeeSearch();
    },

    quickAssignEmployee(employee) {
      this.assignMode = 'individual';
      this.assignForm.employeeId = employee.nationalId || employee.empNo;
      // Scroll to assignment form
      this.$nextTick(() => {
        const assignSection = document.querySelector('.assign-form-grid');
        if (assignSection) {
          assignSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
    },

    changeAssignmentPage(page) {
      this.assignmentPagination.page = page;
      this.fetchAssignments();
    },
  },

  watch: {
    assignmentSearch() {
      if (this.assignmentSearchTimeout) clearTimeout(this.assignmentSearchTimeout);
      this.assignmentSearchTimeout = setTimeout(() => {
        this.assignmentPagination.page = 1;
        this.fetchAssignments();
      }, 300);
    },
    assignmentDeptFilter() {
      this.assignmentPagination.page = 1;
      this.fetchAssignments();
    },
    assignmentUserProfileFilter() {
      this.assignmentPagination.page = 1;
      this.fetchAssignments();
    },
  },
};
</script>

<style scoped>
/* Tab navigation */
.tab-nav {
  display: flex;
  gap: 4px;
  padding: 16px 20px;
  border-bottom: 1px solid #e2e8f0;
  flex-wrap: wrap;
}

.tab-btn {
  padding: 8px 20px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
  color: #64748b;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.tab-btn:hover {
  background: #f1f5f9;
  color: #1e293b;
}

.tab-btn.active {
  background: #059669;
  color: #ffffff;
  border-color: #059669;
  box-shadow: 0 2px 8px rgba(5, 150, 105, 0.3);
}

/* Assignment mode buttons */
.assign-mode-btns {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

/* Assign form grid */
.assign-form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

@media (max-width: 640px) {
  .assign-form-grid {
    grid-template-columns: 1fr;
  }
}

/* Working days checkboxes */
.working-days-grid {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 6px;
}

.day-check-item {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
}

.day-check-item input[type="checkbox"] {
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: #059669;
}

/* Employee checklist */
.employee-checklist {
  max-height: 240px;
  overflow-y: auto;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 8px;
}

.employee-check-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: background 0.15s;
}

.employee-check-item:hover {
  background: #f1f5f9;
}

.employee-check-item input[type="checkbox"] {
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: #059669;
}

/* Modal icons */
.modal-danger-icon {
  font-size: 48px;
  color: #ef4444;
  display: block;
  margin-bottom: 12px;
}

.modal-success-icon {
  font-size: 48px;
  color: #10b981;
  display: block;
  margin-bottom: 12px;
}

/* Alert styles */
.hr-alert {
  padding: 12px 16px;
  border-radius: 10px;
  font-size: 14px;
  display: flex;
  align-items: center;
}

.hr-alert-success {
  background: #d1fae5;
  border: 1px solid #6ee7b7;
  color: #065f46;
}

.hr-alert-danger {
  background: #fee2e2;
  border: 1px solid #fca5a5;
  color: #991b1b;
}

/* Spinner */
.hr-spinner-inline {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid #e2e8f0;
  border-top-color: #059669;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  vertical-align: middle;
  margin-right: 6px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.required {
  color: #ef4444;
}

/* Employee Search Styles */
.employee-search-section {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px;
  background: #f8fafc;
}

.search-results-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  margin-top: 16px;
  overflow: hidden;
}

.search-results-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #f1f5f9;
  border-bottom: 1px solid #e2e8f0;
}

.search-results-header h6 {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
}

.pagination-info {
  font-size: 12px;
  color: #6b7280;
}

.search-results-table {
  max-height: 400px;
  overflow-y: auto;
}

.search-pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
}

.pagination-text {
  font-size: 14px;
  color: #374151;
  font-weight: 500;
}

.hr-btn-xs {
  padding: 4px 8px;
  font-size: 12px;
  line-height: 1.2;
}

.hr-table-sm th,
.hr-table-sm td {
  padding: 8px 12px;
  font-size: 13px;
}

/* Pagination */
.hr-pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
  margin-top: 0;
}

.hr-pagination .pagination-info {
  font-size: 14px;
  color: #374151;
  font-weight: 500;
}
</style>
